import type { IntakeFieldDef } from "@/utils/intakeHelpers";
import { FieldText } from "./fields/FieldText";
import { FieldTextarea } from "./fields/FieldTextarea";
import { FieldYesNo } from "./fields/FieldYesNo";
import { FieldRadio } from "./fields/FieldRadio";
import { FieldMultiSelect } from "./fields/FieldMultiSelect";
import { FieldNumber } from "./fields/FieldNumber";
import { FieldDate } from "./fields/FieldDate";
import { FieldScale } from "./fields/FieldScale";
import { FieldBristolChart, type BristolOption } from "./fields/FieldBristolChart";
import { FieldSymptomGrid } from "./fields/FieldSymptomGrid";

type Props = {
  field: IntakeFieldDef;
  values: Record<string, unknown>;
  setFieldValue: (id: string, value: unknown) => void;
  errors: Record<string, string>;
};

export function IntakeField({ field, values, setFieldValue, errors }: Props) {
  const err = errors[field.id];
  const v = values[field.id];

  switch (field.type) {
    case "text":
      return (
        <FieldText
          id={field.id}
          label={field.label}
          value={v}
          onChange={(x) => setFieldValue(field.id, x)}
          error={err}
        />
      );
    case "textarea":
      return (
        <FieldTextarea
          id={field.id}
          label={field.label}
          value={v}
          onChange={(x) => setFieldValue(field.id, x)}
          error={err}
        />
      );
    case "yes_no":
      return (
        <FieldYesNo
          id={field.id}
          label={field.label}
          value={v}
          onChange={(x) => setFieldValue(field.id, x)}
          error={err}
          note={field.note}
        />
      );
    case "yes_no_with_followup": {
      const follow = field.followUp ?? [];
      return (
        <div className="mb-4">
          <FieldYesNo
            id={field.id}
            label={field.label}
            value={v}
            onChange={(x) => setFieldValue(field.id, x)}
            error={err}
            note={field.note}
          />
          {v === true && follow.length > 0 && (
            <div className="ml-4 mt-3 border-l-2 border-healthsource-navy/30 pl-4">
              {follow.map((f) => (
                <IntakeField
                  key={f.id}
                  field={f}
                  values={values}
                  setFieldValue={setFieldValue}
                  errors={errors}
                />
              ))}
            </div>
          )}
        </div>
      );
    }
    case "radio": {
      const opts = Array.isArray(field.options)
        ? (field.options as string[])
        : [];
      return (
        <FieldRadio
          id={field.id}
          label={field.label}
          options={opts}
          value={v}
          onChange={(x) => setFieldValue(field.id, x)}
          error={err}
        />
      );
    }
    case "multi_select": {
      const opts = Array.isArray(field.options)
        ? (field.options as string[])
        : [];
      return (
        <FieldMultiSelect
          id={field.id}
          label={field.label}
          options={opts}
          value={v}
          onChange={(x) => setFieldValue(field.id, x)}
          error={err}
        />
      );
    }
    case "number":
      return (
        <FieldNumber
          id={field.id}
          label={field.label}
          value={v}
          onChange={(x) => setFieldValue(field.id, x)}
          error={err}
        />
      );
    case "date":
      return (
        <FieldDate
          id={field.id}
          label={field.label}
          value={v}
          onChange={(x) => setFieldValue(field.id, x)}
          error={err}
          note={field.note}
        />
      );
    case "scale":
      return (
        <FieldScale
          id={field.id}
          label={field.label}
          min={field.min ?? 1}
          max={field.max ?? 10}
          labels={field.labels}
          value={v}
          onChange={(x) => setFieldValue(field.id, x)}
          error={err}
          note={field.note}
        />
      );
    case "bristol_chart": {
      const opts = Array.isArray(field.options)
        ? (field.options as BristolOption[])
        : [];
      return (
        <FieldBristolChart
          id={field.id}
          label={field.label}
          options={opts}
          value={v}
          onChange={(x) => setFieldValue(field.id, x)}
          error={err}
        />
      );
    }
    case "symptom_grid": {
      const opts = Array.isArray(field.options)
        ? (field.options as string[])
        : [];
      return (
        <FieldSymptomGrid
          id={field.id}
          label={field.label}
          options={opts}
          value={v}
          onChange={(x) => setFieldValue(field.id, x)}
          error={err}
        />
      );
    }
    case "info":
      return (
        <p className="mb-4 text-sm text-slate-600">{field.label}</p>
      );
    default:
      return (
        <p className="mb-4 text-xs text-amber-700">
          Unsupported field type: {field.type}
        </p>
      );
  }
}
